import { useTranslation } from "react-i18next";

import { useIsMobile } from "@/core/hooks";
import { SUPPORTED_LOCALES, type SupportedLocale } from "@/core/i18n";
import { Button, Infobulle, Select, useMenu } from "@/shared/ui";

/**
 * Bouton + menu "Change language" — pendant du ThemePickerButton dans la
 * BottomBar. Click → le `Menu` de la librairie, sous le bouton. Une ligne par
 * langue : drapeau + nom de la langue dans sa propre langue, la coche de la
 * marque sur la langue courante. Le choix est persiste par
 * i18next-browser-languagedetector dans localStorage (`cockpit.locale`) —
 * meme source de verite que le picker dans Settings → Preferences, donc les
 * deux restent synchronises automatiquement.
 */
const FLAGS: Record<SupportedLocale, string> = {
  en: "🇬🇧",
  fr: "🇫🇷",
  es: "🇪🇸",
  it: "🇮🇹",
};

export function LanguagePickerButton({
  variant = "bar",
}: {
  /** "bar" = libellé inline (BottomBar) ; "rail" = icône seule (SideRail). */
  variant?: "bar" | "rail";
} = {}) {
  const { t, i18n } = useTranslation();
  const { ouvrirSous, ouvert, menu } = useMenu();
  const isMobile = useIsMobile();
  const isRail = variant === "rail" && !isMobile;

  const current = ((i18n.resolvedLanguage ?? i18n.language ?? "en").slice(0, 2)) as SupportedLocale;

  async function pick(code: SupportedLocale) {
    if (code !== current) await i18n.changeLanguage(code);
  }

  function ouvrir(ancre: HTMLElement) {
    ouvrirSous(
      ancre,
      SUPPORTED_LOCALES.map((code) => ({
        label: t(`shell.settings.preferences.languages.${code}`),
        icone: (
          <span aria-hidden className="text-base leading-none">
            {FLAGS[code]}
          </span>
        ),
        coche: code === current,
        onClick: () => void pick(code),
      })),
      { alignement: isRail || isMobile ? "gauche" : "droite" },
    );
  }

  const libelle = t("shell.languagePicker.ariaLabel");
  // Code pays (FR/EN/ES) en lettres — remplace l'ancienne icône planète
  // (demande user : « mettre les lettres du pays »).
  const lettres = t(`shell.languagePicker.shortLabel.${current}`).toUpperCase();

  return (
    <>
      {isMobile ? (
        <button
          type="button"
          onClick={(e) => ouvrir(e.currentTarget)}
          aria-haspopup="menu"
          aria-expanded={ouvert}
          aria-label={libelle}
          className="mobile-tab-item h-12"
        >
          <span className="text-[15px] font-bold leading-none">{lettres}</span>
        </button>
      ) : isRail ? (
        <Infobulle texte={libelle} cote="droite">
          <Button
            variant="ghost"
            size="lg"
            iconOnly
            className="h-9 w-9"
            onClick={(e) => ouvrir(e.currentTarget)}
            aria-haspopup="menu"
            aria-expanded={ouvert}
            aria-label={libelle}
          >
            <span className="text-[12px] font-bold leading-none">{lettres}</span>
          </Button>
        </Infobulle>
      ) : (
        <Infobulle texte={libelle}>
          <Button
            variant="ghost"
            size="md"
            onClick={(e) => ouvrir(e.currentTarget)}
            aria-haspopup="menu"
            aria-expanded={ouvert}
            aria-label={libelle}
          >
            <span className="font-bold leading-none">{lettres}</span>
          </Button>
        </Infobulle>
      )}
      {menu}
    </>
  );
}

/**
 * La langue, pour le volet du Thème : dans le kiosque, la langue n'a plus
 * d'icône à elle dans le rail (Kayla, 07-10-2026 : « juste l'icône Thème, et
 * tout mettre dans le truc à droite »). Un `Select` — quatre langues avec
 * leur drapeau tiennent à l'aise dans 300 px, ce qu'une rangée de segments
 * ne fait pas.
 */
export function ChoixDeLaLangue() {
  const { t, i18n } = useTranslation();
  const current = ((i18n.resolvedLanguage ?? i18n.language ?? "en").slice(0, 2)) as SupportedLocale;
  const NOMS: Record<SupportedLocale, string> = {
    en: "English",
    fr: "Français",
    es: "Español",
    it: "Italiano",
  };
  return (
    <Select
      aria-label={t("shell.languagePicker.ariaLabel")}
      value={current}
      onChange={(e) => void i18n.changeLanguage(e.target.value)}
    >
      {SUPPORTED_LOCALES.map((code) => (
        <option key={code} value={code}>
          {`${FLAGS[code]}  ${NOMS[code]}`}
        </option>
      ))}
    </Select>
  );
}
